import type { ReactNode } from 'react';
import type { Allowance, RuleConfig, Tier } from '@em/shared';
import { useCategories } from '../../api';
import { Button, Checkbox, Field, Input, Select } from '../../components/ui';

type DayKey = keyof RuleConfig['dayTiers'];
const DAY_LABELS: Record<DayKey, string> = { weekday: 'Weekday', saturday: 'Saturday', sunday: 'Sunday', publicHoliday: 'Public holiday' };
const DAY_TYPES = ['weekday', 'saturday', 'sunday', 'publicHoliday'] as const;
const SITE_KINDS = ['factory', 'install', 'supplier', 'other'] as const;

/** Number input that maps an empty box to null when `nullable`. */
function Num({ value, onChange, nullable, step = 'any', min }: { value: number | null; onChange: (v: number | null) => void; nullable?: boolean; step?: string; min?: number }) {
  return (
    <Input
      type="number"
      step={step}
      min={min}
      value={value ?? ''}
      placeholder={nullable ? 'None' : undefined}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') onChange(nullable ? null : 0);
        else onChange(Number(raw));
      }}
    />
  );
}

function CategorySelect({ value, onChange, nullable, codes }: { value: string | null; onChange: (v: string | null) => void; nullable?: boolean; codes: string[] }) {
  const all = value && !codes.includes(value) ? [...codes, value] : codes;
  return (
    <Select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
      {nullable && <option value="">None</option>}
      {all.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </Select>
  );
}

function Section({ title, children, hint }: { title: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <fieldset>
      <legend>{title}</legend>
      {hint && <p className="small muted">{hint}</p>}
      {children}
    </fieldset>
  );
}

/** Structured editor for a complete RuleConfig. Validation happens on save with ruleConfigSchema. */
export function RuleConfigForm({ value, onChange }: { value: RuleConfig; onChange: (v: RuleConfig) => void }) {
  const cats = useCategories();
  const codes = cats.data?.map((c) => c.code) ?? ['ORD', 'OT1', 'OT2', 'SAT', 'SUN', 'PH'];
  const hourCodes = cats.data ? cats.data.filter((c) => c.unit === 'hours').map((c) => c.code) : codes;
  const unitCodes = cats.data ? cats.data.filter((c) => c.unit === 'units').map((c) => c.code) : codes;
  const v = value;
  const patch = <K extends keyof RuleConfig>(k: K, p: Partial<RuleConfig[K]>) => onChange({ ...v, [k]: { ...(v[k] as object), ...p } as RuleConfig[K] });

  const setTiers = (day: DayKey, tiers: Tier[]) => onChange({ ...v, dayTiers: { ...v.dayTiers, [day]: tiers } });
  const setAllowance = (i: number, a: Partial<Allowance>) => onChange({ ...v, allowances: v.allowances.map((x, j) => (j === i ? { ...x, ...a } : x)) });

  return (
    <div className="stack">
      <Section title="Ordinary hours and span">
        <div className="form-grid three">
          <Field label="Max ordinary hours per week">
            <Num value={v.ordinary.maxWeeklyHours} onChange={(n) => patch('ordinary', { maxWeeklyHours: n ?? 0 })} />
          </Field>
          <Field label="Weekly overtime category" hint="Hours past the weekly maximum">
            <CategorySelect nullable codes={hourCodes} value={v.ordinary.weeklyOvertimeCategory} onChange={(c) => patch('ordinary', { weeklyOvertimeCategory: c })} />
          </Field>
          <Field label="Ordinary category">
            <CategorySelect codes={hourCodes} value={v.ordinary.ordinaryCategory} onChange={(c) => patch('ordinary', { ordinaryCategory: c ?? 'ORD' })} />
          </Field>
          <Field label="Span start" hint="Empty = no span">
            <Input type="time" value={v.ordinary.spanStart ?? ''} onChange={(e) => patch('ordinary', { spanStart: e.target.value || null })} />
          </Field>
          <Field label="Span end">
            <Input type="time" value={v.ordinary.spanEnd ?? ''} onChange={(e) => patch('ordinary', { spanEnd: e.target.value || null })} />
          </Field>
          <Field label="Outside-span category">
            <CategorySelect nullable codes={hourCodes} value={v.ordinary.outsideSpanCategory} onChange={(c) => patch('ordinary', { outsideSpanCategory: c })} />
          </Field>
        </div>
      </Section>

      <Section title="Day tiers" hint="Hours worked in a day fill the tiers in order. Each limit is cumulative; the last tier has no limit.">
        <div className="grid grid-2">
          {(Object.keys(DAY_LABELS) as DayKey[]).map((day) => {
            const tiers = v.dayTiers[day];
            return (
              <div key={day}>
                <h3 style={{ marginBottom: 6 }}>{DAY_LABELS[day]}</h3>
                {tiers.map((t, i) => (
                  <div className="tier-row" key={i}>
                    <Field label={i === tiers.length - 1 ? 'Up to (last tier)' : `Up to hours`}>
                      <Num
                        nullable
                        value={t.upToHours}
                        onChange={(n) => setTiers(day, tiers.map((x, j) => (j === i ? { ...x, upToHours: n } : x)))}
                      />
                    </Field>
                    <Field label="Category">
                      <CategorySelect codes={hourCodes} value={t.category} onChange={(c) => setTiers(day, tiers.map((x, j) => (j === i ? { ...x, category: c ?? 'ORD' } : x)))} />
                    </Field>
                    <Button size="sm" variant="ghost" disabled={tiers.length === 1} onClick={() => setTiers(day, tiers.filter((_, j) => j !== i))} aria-label={`Remove ${DAY_LABELS[day]} tier ${i + 1}`}>
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm"
                  onClick={() => {
                    const last = tiers.at(-1);
                    const prevLimit = tiers.length > 1 ? (tiers.at(-2)?.upToHours ?? 0) : 0;
                    const closed = tiers.slice(0, -1).concat(last ? [{ ...last, upToHours: prevLimit + 2 }] : []);
                    setTiers(day, [...closed, { upToHours: null, category: 'OT2' }]);
                  }}
                >
                  + Add tier
                </Button>
              </div>
            );
          })}
        </div>
        <div className="form-grid three" style={{ marginTop: 12 }}>
          <Field label="Public holiday minimum hours" hint="Empty = none">
            <Num nullable value={v.publicHolidayMinimumHours} onChange={(n) => onChange({ ...v, publicHolidayMinimumHours: n })} />
          </Field>
        </div>
      </Section>

      <Section title="Breaks">
        <div className="form-grid three">
          <div className="field" style={{ justifyContent: 'flex-end', paddingBottom: 8 }}>
            <Checkbox label="Rest breaks are paid" checked={v.breaks.restBreaksPaid} onChange={(b) => patch('breaks', { restBreaksPaid: b })} />
          </div>
          <Field label="Minimum meal break (minutes)">
            <Num step="1" min={0} value={v.breaks.minMealMinutes} onChange={(n) => patch('breaks', { minMealMinutes: n ?? 0 })} />
          </Field>
          <Field label="Meal required by (hours worked)" hint="Empty = not checked">
            <Num nullable value={v.breaks.mealRequiredByHours} onChange={(n) => patch('breaks', { mealRequiredByHours: n })} />
          </Field>
          <Field label="Auto-deduct after (hours)" hint="When no meal break was punched; empty = never">
            <Num nullable value={v.breaks.autoDeductAfterHours} onChange={(n) => patch('breaks', { autoDeductAfterHours: n })} />
          </Field>
          <Field label="Auto-deduct (minutes)">
            <Num step="1" min={0} value={v.breaks.autoDeductMinutes} onChange={(n) => patch('breaks', { autoDeductMinutes: n ?? 0 })} />
          </Field>
        </div>
      </Section>

      <div className="grid grid-2">
        <Section title="Rounding">
          <div className="form-grid">
            <Field label="Mode">
              <Select value={v.rounding.mode} onChange={(e) => patch('rounding', { mode: e.target.value as RuleConfig['rounding']['mode'] })}>
                <option value="none">No rounding</option>
                <option value="nearest">Nearest increment</option>
                <option value="employee">In the employee's favour</option>
              </Select>
            </Field>
            <Field label="Increment (minutes)">
              <Select value={v.rounding.incrementMinutes} onChange={(e) => patch('rounding', { incrementMinutes: Number(e.target.value) as RuleConfig['rounding']['incrementMinutes'] })}>
                {[1, 5, 6, 15].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Apply to">
              <Select value={v.rounding.applyTo} onChange={(e) => patch('rounding', { applyTo: e.target.value as RuleConfig['rounding']['applyTo'] })}>
                <option value="punch">Each punch</option>
                <option value="shift">Shift total</option>
              </Select>
            </Field>
          </div>
        </Section>
        <Section title="Grace and early starts">
          <div className="form-grid">
            <Field label="Late grace (minutes)">
              <Num step="1" min={0} value={v.grace.lateMinutes} onChange={(n) => patch('grace', { lateMinutes: n ?? 0 })} />
            </Field>
            <Field label="Early start grace (minutes)">
              <Num step="1" min={0} value={v.grace.earlyMinutes} onChange={(n) => patch('grace', { earlyMinutes: n ?? 0 })} />
            </Field>
            <Field label="Early finish grace (minutes)">
              <Num step="1" min={0} value={v.grace.earlyFinishMinutes} onChange={(n) => patch('grace', { earlyFinishMinutes: n ?? 0 })} />
            </Field>
            <Field label="Early start before roster">
              <Select value={v.grace.earlyStartPolicy} onChange={(e) => patch('grace', { earlyStartPolicy: e.target.value as RuleConfig['grace']['earlyStartPolicy'] })}>
                <option value="clip_to_roster">Pay from rostered start</option>
                <option value="paid">Pay from clock-in</option>
              </Select>
            </Field>
          </div>
        </Section>
      </div>

      <div className="grid grid-2">
        <Section title="Minimum engagement and shift limits">
          <div className="form-grid">
            <Field label="Minimum engagement (hours)" hint="Empty = none">
              <Num nullable value={v.minimumEngagementHours} onChange={(n) => onChange({ ...v, minimumEngagementHours: n })} />
            </Field>
            <Field label="Long shift warning (hours)">
              <Num value={v.shiftLimits.longShiftHours} onChange={(n) => patch('shiftLimits', { longShiftHours: n ?? 0 })} />
            </Field>
            <Field label="Auto clock-out after (hours)">
              <Num value={v.shiftLimits.autoClockOutHours} onChange={(n) => patch('shiftLimits', { autoClockOutHours: n ?? 0 })} />
            </Field>
          </div>
        </Section>
        <Section title="Roster policy">
          <div className="form-grid">
            <Field label="Unrostered work">
              <Select value={v.rosterPolicy.unrosteredWorkSeverity} onChange={(e) => patch('rosterPolicy', { unrosteredWorkSeverity: e.target.value as RuleConfig['rosterPolicy']['unrosteredWorkSeverity'] })}>
                <option value="blocking">Blocking</option>
                <option value="review">Review</option>
                <option value="off">Ignore</option>
              </Select>
            </Field>
            <Field label="Rostered but absent">
              <Select value={v.rosterPolicy.absentSeverity} onChange={(e) => patch('rosterPolicy', { absentSeverity: e.target.value as RuleConfig['rosterPolicy']['absentSeverity'] })}>
                <option value="review">Review</option>
                <option value="info">Info</option>
                <option value="off">Ignore</option>
              </Select>
            </Field>
          </div>
        </Section>
      </div>

      <Section title="Allowances">
        {v.allowances.length === 0 && <p className="muted small">No allowances.</p>}
        {v.allowances.map((a, i) => (
          <div key={i} className="card" style={{ padding: 12, marginBottom: 8 }}>
            <div className="form-grid three">
              <Field label="Code">
                <Input value={a.code} onChange={(e) => setAllowance(i, { code: e.target.value })} />
              </Field>
              <Field label="Category">
                <CategorySelect codes={unitCodes.length ? unitCodes : codes} value={a.category} onChange={(c) => setAllowance(i, { category: c ?? '' })} />
              </Field>
              <Field label="Per">
                <Select value={a.per} onChange={(e) => setAllowance(i, { per: e.target.value as Allowance['per'] })}>
                  <option value="shift">Shift</option>
                  <option value="day">Day</option>
                </Select>
              </Field>
              <Field label="Quantity">
                <Num value={a.quantity} onChange={(n) => setAllowance(i, { quantity: n ?? 1 })} />
              </Field>
              <Field label="Minimum worked hours" hint="Empty = any">
                <Num nullable value={a.minWorkedHours} onChange={(n) => setAllowance(i, { minWorkedHours: n })} />
              </Field>
              <div className="field">
                <span className="label">Only at site kinds</span>
                <div className="row">
                  {SITE_KINDS.map((k) => (
                    <Checkbox
                      key={k}
                      label={k}
                      checked={a.siteKinds?.includes(k) ?? false}
                      onChange={(on) => {
                        const next = new Set(a.siteKinds ?? []);
                        if (on) next.add(k);
                        else next.delete(k);
                        setAllowance(i, { siteKinds: next.size ? [...next] : null });
                      }}
                    />
                  ))}
                </div>
                <span className="hint">None ticked = any site</span>
              </div>
              <div className="field full">
                <span className="label">Only on days</span>
                <div className="row">
                  {DAY_TYPES.map((k) => (
                    <Checkbox
                      key={k}
                      label={DAY_LABELS[k]}
                      checked={a.dayTypes?.includes(k) ?? false}
                      onChange={(on) => {
                        const next = new Set(a.dayTypes ?? []);
                        if (on) next.add(k);
                        else next.delete(k);
                        setAllowance(i, { dayTypes: next.size ? [...next] : null });
                      }}
                    />
                  ))}
                  <span className="spacer" />
                  <Button size="sm" variant="danger" onClick={() => onChange({ ...v, allowances: v.allowances.filter((_, j) => j !== i) })}>
                    Remove allowance
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
        <Button
          size="sm"
          onClick={() =>
            onChange({
              ...v,
              allowances: [...v.allowances, { code: 'NEW', category: unitCodes[0] ?? 'ALLOW_MEAL', per: 'shift', quantity: 1, minWorkedHours: null, siteKinds: null, dayTypes: null }],
            })
          }
        >
          + Add allowance
        </Button>
      </Section>
    </div>
  );
}
